// web/src/store/hedgeAccounting/thunks.ts
import toastMessage from '@/components/Toast';
import { getThunkError } from '@/store/utility/thunkErrorUtils';
import apiInstance from '@api/index';
import { createAsyncThunk } from '@reduxjs/toolkit';
import { AppDispatch } from '@store/store';

import {
  removeLocalAmortRow,
  setTransactionAmortSchedules,
  upsertLocalAmortRow,
} from './hedgeAccountingSlice';
import {
  BulkTransactionTitle,
  BulkTransactionTitlesRequest,
  BulkTransactionTitlesResponse,
  CalculateAmortEffectiveRatePayload,
  CompanyEntityHedgingStandard,
  CreateJournalEntryMovementsPayload,
  CreateTransactionAmortPayload,
  CreateTransactionAmortSetSchedulePayload,
  CreateTransactionNoHedgeAccPayload,
  EditJournalEntryMovementCommentPayload,
  HedgeAccountingState,
  HedgeAmortData,
  InitAmortEffectiveRatePayload,
  InitAmortEffectiveRateResponse,
  JournalEntry,
  JournalEntryCommand,
  JournalEntryMovement,
  JournalEntryMovementStandard,
  PatchTransactionAmortPayload,
  PatchTransactionEffectivenessPayload,
  SearchTransactionAmortSetSchedulePayload,
  TradeDesignationDate,
  TransactionAmortSetScheduleData,
  TransactionAmortType,
  TransactionEffectivenessData,
  TransactionEffectivenessPayload,
  TransactionLeg,
  TransactionRelation,
  TransactionTitle,
  TransactionTitlePayload,
  TransactionWithoutHedgeAcc,
  UpdateTransactionAmortPayload,
  UpdateTransactionEffectivenessPayload,
  UpdateTransactionFieldsPayload,
  UpdateTransactionNoHedgeAccPayload,
} from './types';

export const fetchJournalEntries = createAsyncThunk<
  JournalEntry[],
  string | undefined,
  { state: { hedgeAccounting: HedgeAccountingState }; rejectValue: string }
>(
  'hedgeAccounting/fetchJournalEntries',
  async (tradeId, { getState, rejectWithValue }) => {
    try {
      const state = getState() as { hedgeAccounting: HedgeAccountingState };
      const { selectedTrades, selectedEntity } = state.hedgeAccounting;

      // Prepare parameters for the API call
      const params: {
        transaction_ids?: string[];
        company_entity_ids?: string[];
      } = {};

      // If a specific tradeId is provided, use that instead of selections
      if (tradeId) {
        params.transaction_ids = [tradeId];
      }
      // Otherwise use selections from state
      else if (selectedTrades.length > 0) {
        params.transaction_ids = selectedTrades.map((trade) =>
          String(trade.value),
        );
      }

      // Only include company_entity_ids if there are selected entities and no specific tradeId
      if (!tradeId && selectedEntity.length > 0) {
        params.company_entity_ids = selectedEntity.map((entity) =>
          String(entity.value),
        );
      }

      // Check if at least one parameter has values
      if (
        Object.keys(params).length === 0 ||
        (params.transaction_ids === undefined &&
          params.company_entity_ids === undefined)
      ) {
        return rejectWithValue('No trades or entities selected');
      }

      const response =
        await apiInstance.hedgeAccounting.getJournalEntries(params);

      // The API returns an array of journal entries directly
      if (Array.isArray(response.data)) {
        return response.data;
      }

      // Handle nested success/data structure if present
      if (
        response.data &&
        response.data.success &&
        Array.isArray(response.data.data)
      ) {
        return response.data.data;
      }

      // If it's a single object (not in an array), wrap it in an array
      if (
        response.data &&
        typeof response.data === 'object' &&
        !Array.isArray(response.data)
      ) {
        // Check if it has the expected journal entry structure
        if ('trade_id' in response.data && 'je_data' in response.data) {
          return [response.data];
        }
      }

      // If response structure is unexpected, return an empty array
      return [];
    } catch (error: any) {
      return rejectWithValue(
        error.message || 'Failed to fetch journal entries',
      );
    }
  },
);

export const fetchJournalEntryTypes = createAsyncThunk(
  'hedgeAccounting/fetchJournalEntryTypes',
  async (_, { rejectWithValue }) => {
    try {
      const api = apiInstance.hedgeAccounting;
      const response = await api.getJournalEntryTypes();
      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        error?.response?.data || error?.message || 'Unknown error',
      );
    }
  },
);

export const fetchEffectivenessTypes = createAsyncThunk(
  'hedgeAccounting/fetchEffectivenessTypes',
  async (_, { rejectWithValue }) => {
    try {
      const response = await apiInstance.hedgeAccounting.getEffectivenessType();
      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        error.message || 'Failed to fetch effectiveness types',
      );
    }
  },
);

export const fetchTransactionAmortTypes = createAsyncThunk<
  TransactionAmortType[],
  void,
  { rejectValue: string }
>(
  'hedgeAccounting/fetchTransactionAmortTypes',
  async (_, { rejectWithValue }) => {
    try {
      const response =
        await apiInstance.hedgeAccounting.getTransactionAmortType();
      // API directly returns an array of TransactionAmortType objects
      if (Array.isArray(response.data)) {
        return response.data;
      }
      return rejectWithValue('Invalid response format from API');
    } catch (error: any) {
      return rejectWithValue(
        error.message || 'Failed to fetch transaction amortization types',
      );
    }
  },
);

export const fetchJournalEntryMovementStandards = createAsyncThunk<
  JournalEntryMovementStandard[],
  void,
  { rejectValue: string }
>(
  'hedgeAccounting/fetchJournalEntryMovementStandards',
  async (_, { rejectWithValue }) => {
    try {
      const response =
        await apiInstance.hedgeAccounting.getJournalEntryMovementStandards();
      if (Array.isArray(response.data)) {
        return response.data;
      }
      if (
        response.data &&
        response.data.success &&
        Array.isArray(response.data.data)
      ) {
        return response.data.data;
      }
      return rejectWithValue('Invalid response format from API');
    } catch (error: any) {
      return rejectWithValue(
        error.message || 'Failed to fetch journal entry movement standards',
      );
    }
  },
);

export const fetchHedgeAmortData = createAsyncThunk<
  HedgeAmortData[],
  string | undefined,
  {
    state: { hedgeAccounting: HedgeAccountingState };
    rejectValue: string;
    dispatch: AppDispatch;
  }
>(
  'hedgeAccounting/fetchHedgeAmortData',
  async (runDate = undefined, { getState, rejectWithValue }) => {
    try {
      const state = getState() as { hedgeAccounting: HedgeAccountingState };
      const { selectedTrades, selectedEntity } = state.hedgeAccounting;

      // Prepare parameters for the API call
      const params: {
        transaction_ids?: string[];
        company_entity_ids?: string[];
        run_date?: string;
      } = {};

      // Only include transaction_ids if there are selected trades
      if (selectedTrades.length > 0) {
        params.transaction_ids = selectedTrades.map((trade) =>
          String(trade.value),
        );
      }

      // Only include company_entity_ids if there are selected entities
      if (selectedEntity.length > 0) {
        params.company_entity_ids = selectedEntity.map((entity) =>
          String(entity.value),
        );
      }

      // Add run_date parameter if provided
      if (runDate) {
        params.run_date = runDate;
      }

      // Check if at least one parameter has values
      if (
        Object.keys(params).length === 0 ||
        (params.transaction_ids === undefined &&
          params.company_entity_ids === undefined)
      ) {
        return rejectWithValue('No trades or entities selected');
      }

      const response =
        await apiInstance.hedgeAccounting.fetchHedgeAmortData(params);

      // The response has the structure from Axios with nested data.data
      if (
        response.data &&
        response.data.success &&
        Array.isArray(response.data.data)
      ) {
        return response.data.data;
      }

      // If response structure is unexpected, return an empty array
      return [];
    } catch (error: any) {
      return rejectWithValue(
        error.message || 'Failed to fetch hedge amortization data',
      );
    }
  },
);

export const createTransactionAmort = createAsyncThunk<
  HedgeAmortData,
  { payload: CreateTransactionAmortPayload; tempId: number },
  { rejectValue: string; dispatch: AppDispatch }
>(
  'hedgeAccounting/createTransactionAmort',
  async ({ payload, tempId }, { rejectWithValue, dispatch }) => {
    try {
      const response =
        await apiInstance.hedgeAccounting.createTransactionAmort(payload);

      // Handle empty response (200 but no data)
      if (!response.data) {
        return rejectWithValue('Empty response from API');
      }

      let apiData: any;

      // Handle success/data structure
      if (
        typeof response.data === 'object' &&
        'success' in response.data &&
        response.data.success === true &&
        'data' in response.data
      ) {
        // Handle empty data object
        if (!response.data.data || Object.keys(response.data.data).length === 0) {
          return rejectWithValue('Empty data returned from API');
        }
        apiData = response.data.data;
      }
      // Handle direct data response
      else if (typeof response.data === 'object' && !('success' in response.data)) {
        // Handle empty object
        if (Object.keys(response.data).length === 0) {
          return rejectWithValue('Empty data returned from API');
        }
        apiData = response.data;
      }
      // Handle success: false case
      else if (
        typeof response.data === 'object' &&
        'success' in response.data &&
        response.data.success === false
      ) {
        return rejectWithValue(
          response.data.error_msg || 'API returned success: false'
        );
      }
      else {
        return rejectWithValue('Invalid response format from API');
      }

      // Transform the API response to HedgeAmortData format
      const transformed: HedgeAmortData = {
        ...apiData,
        je_type_id: apiData.je_type ?? apiData.je_type_id,
        amort_schedule_transaction_id: apiData.amort_schedule_transaction ?? apiData.amort_schedule_transaction_id,
        amort_schedule_type_id: apiData.amort_schedule_type ?? apiData.amort_schedule_type_id,
      };

      await dispatch(removeLocalAmortRow(tempId));

      dispatch(upsertLocalAmortRow({ data: transformed, isNew: true }));

      toastMessage.success('Transaction amortization created successfully');

      return transformed;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.error_msg ||
          error.message ||
          'Failed to create transaction amortization',
      );
    }
  },
);

export const updateTransactionAmort = createAsyncThunk<
  HedgeAmortData,
  UpdateTransactionAmortPayload,
  { rejectValue: string; dispatch: AppDispatch }
>(
  'hedgeAccounting/updateTransactionAmort',
  async (payload, { rejectWithValue, dispatch }) => {
    try {
      const response = await apiInstance.hedgeAccounting.updateTransactionAmort(
        payload.hedge_acc_amort_id,
        payload.data,
      );

      // Handle empty response (200 but no data)
      if (!response.data) {
        return rejectWithValue('Empty response from API');
      }

      let apiData: any;

      // Handle success/data structure
      if (
        typeof response.data === 'object' &&
        'success' in response.data &&
        response.data.success === true &&
        'data' in response.data
      ) {
        // Handle empty data object
        if (!response.data.data || Object.keys(response.data.data).length === 0) {
          return rejectWithValue('Empty data returned from API');
        }
        apiData = response.data.data;
      }
      // Handle direct data response
      else if (typeof response.data === 'object' && !('success' in response.data)) {
        // Handle empty object
        if (Object.keys(response.data).length === 0) {
          return rejectWithValue('Empty data returned from API');
        }
        apiData = response.data;
      }
      // Handle success: false case
      else if (
        typeof response.data === 'object' &&
        'success' in response.data &&
        response.data.success === false
      ) {
        return rejectWithValue(
          response.data.error_msg || 'API returned success: false'
        );
      }
      else {
        return rejectWithValue('Invalid response format from API');
      }

      // Transform the API response to HedgeAmortData format
      const transformed: HedgeAmortData = {
        ...apiData,
        je_type_id: apiData.je_type ?? apiData.je_type_id,
        amort_schedule_transaction_id: apiData.amort_schedule_transaction ?? apiData.amort_schedule_transaction_id,
        amort_schedule_type_id: apiData.amort_schedule_type ?? apiData.amort_schedule_type_id,
      };

      dispatch(upsertLocalAmortRow({ data: transformed, isNew: false }));

      toastMessage.success('Transaction amortization updated successfully');

      return transformed;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.error_msg ||
          error.message ||
          'Failed to update transaction amortization',
      );
    }
  },
);

export const patchTransactionAmort = createAsyncThunk<
  HedgeAmortData,
  PatchTransactionAmortPayload,
  { rejectValue: string }
>(
  'hedgeAccounting/patchTransactionAmort',
  async (payload, { rejectWithValue }) => {
    try {
      const response = await apiInstance.hedgeAccounting.patchTransactionAmort(
        payload.hedge_acc_amort_id,
        payload.data,
      );

      // Handle empty response (200 but no data)
      if (!response.data) {
        return rejectWithValue('Empty response from API');
      }

      // Handle success/data structure
      if (
        typeof response.data === 'object' &&
        'success' in response.data &&
        response.data.success === true &&
        'data' in response.data
      ) {
        // Handle empty data object
        if (!response.data.data || Object.keys(response.data.data).length === 0) {
          return rejectWithValue('Empty data returned from API');
        }
        toastMessage.success('Transaction amortization patched successfully');
        return response.data.data;
      }
      // Handle direct data response
      else if (typeof response.data === 'object' && !('success' in response.data)) {
        // Handle empty object
        if (Object.keys(response.data).length === 0) {
          return rejectWithValue('Empty data returned from API');
        }
        toastMessage.success('Transaction amortization patched successfully');
        return response.data;
      }
      // Handle success: false case
      else if (
        typeof response.data === 'object' &&
        'success' in response.data &&
        response.data.success === false
      ) {
        return rejectWithValue(
          response.data.error_msg || 'API returned success: false'
        );
      }

      return rejectWithValue('Invalid response format from API');
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.error_msg ||
          error.message ||
          'Failed to patch transaction amortization',
      );
    }
  },
);

export const deleteTransactionAmort = createAsyncThunk<
  number,
  number,
  { rejectValue: string; dispatch: AppDispatch }
>(
  'hedgeAccounting/deleteTransactionAmort',
  async (hedge_acc_amort_id, { rejectWithValue, dispatch }) => {
    try {
      await apiInstance.hedgeAccounting.deleteTransactionAmort(
        hedge_acc_amort_id,
      );
      dispatch(removeLocalAmortRow(hedge_acc_amort_id));

      toastMessage.success('Transaction amortization deleted successfully');

      return hedge_acc_amort_id;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.error_msg ||
          error.message ||
          'Failed to delete transaction amortization',
      );
    }
  },
);

export const calculateAmortEffectiveRate = createAsyncThunk<
  HedgeAmortData,
  CalculateAmortEffectiveRatePayload,
  { rejectValue: string; dispatch: AppDispatch }
>(
  'hedgeAccounting/calculateAmortEffectiveRate',
  async (payload, { rejectWithValue }) => {
    try {
      const response =
        await apiInstance.hedgeAccounting.calculateAmortEffectiveRate(
          payload.hedge_acc_amort_id,
          payload.run_date,
        );

      // Handle empty response (200 but no data)
      if (!response.data) {
        return rejectWithValue('Empty response from API');
      }

      let apiData: any;

      // Handle success/data structure
      if (
        typeof response.data === 'object' &&
        'success' in response.data &&
        response.data.success === true &&
        'data' in response.data
      ) {
        // Handle empty data object
        if (!response.data.data || Object.keys(response.data.data).length === 0) {
          return rejectWithValue('Empty data returned from API');
        }
        apiData = response.data.data;
      }
      // Handle direct data response
      else if (typeof response.data === 'object' && !('success' in response.data)) {
        // Handle empty object
        if (Object.keys(response.data).length === 0) {
          return rejectWithValue('Empty data returned from API');
        }
        apiData = response.data;
      }
      // Handle success: false case
      else if (
        typeof response.data === 'object' &&
        'success' in response.data &&
        response.data.success === false
      ) {
        return rejectWithValue(
          response.data.error_msg || 'API returned success: false'
        );
      }
      else {
        return rejectWithValue('Invalid response format from API');
      }

      // Transform the API response to HedgeAmortData format
      const transformed: HedgeAmortData = {
        ...apiData,
        je_type_id: apiData.je_type ?? apiData.je_type_id,
        amort_schedule_transaction_id: apiData.amort_schedule_transaction ?? apiData.amort_schedule_transaction_id,
        amort_schedule_type_id: apiData.amort_schedule_type ?? apiData.amort_schedule_type_id,
      };

      toastMessage.success('Amortization effective rate calculated successfully');

      return transformed;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.error_msg ||
          error.message ||
          'Failed to calculate amortization effective rate',
      );
    }
  },
);

export const fetchTransactionAmortSetSchedule = createAsyncThunk<
  TransactionAmortSetScheduleData[],
  number,
  { rejectValue: string }
>(
  'hedgeAccounting/fetchTransactionAmortSetSchedule',
  async (hedge_acc_amort_id, { rejectWithValue }) => {
    try {
      const response =
        await apiInstance.hedgeAccounting.fetchTransactionAmortSetSchedule(
          hedge_acc_amort_id,
        );

      // Handle different response formats
      if (Array.isArray(response.data)) {
        return response.data;
      }

      if (response.data && typeof response.data === 'object') {
        if (
          'success' in response.data &&
          response.data.success &&
          Array.isArray(response.data.data)
        ) {
          return response.data.data;
        }
      }

      return [];
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.error_msg ||
          error.message ||
          'Failed to fetch transaction amortization schedule',
      );
    }
  },
);

export const createTransactionAmortSetSchedule = createAsyncThunk<
  void,
  CreateTransactionAmortSetSchedulePayload,
  { rejectValue: string; dispatch: AppDispatch }
>(
  'hedgeAccounting/createTransactionAmortSetSchedule',
  async (payload, { rejectWithValue, dispatch }) => {
    try {
      // 1) Create the schedule entry on the server
      await apiInstance.hedgeAccounting.createTransactionAmortSetSchedule(
        payload,
      );

      // 2) Reload the schedule list for this amort ID
      dispatch(
        searchTransactionAmortSetSchedule({
          hedge_acc_trans_amort_id: payload.amort_id,
        }),
      );
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.error_msg ||
          error.message ||
          'Failed to create transaction amortization schedule',
      );
    }
  },
);

export const searchTransactionAmortSetSchedule = createAsyncThunk<
  Record<number, TransactionAmortSetScheduleData[]>,
  SearchTransactionAmortSetSchedulePayload,
  { rejectValue: string; dispatch: AppDispatch }
>(
  'hedgeAccounting/searchTransactionAmortSetSchedule',
  async (payload, { rejectWithValue, dispatch }) => {
    try {
      const response =
        await apiInstance.hedgeAccounting.searchTransactionAmortSetSchedule(
          payload,
        );

      // normalize into Record<amortId, data[]>
      let result: Record<number, TransactionAmortSetScheduleData[]> = {};
      if (
        response.data &&
        typeof response.data === 'object' &&
        response.data.success &&
        Array.isArray(response.data.data)
      ) {
        const items = response.data.data;
        const amortId = payload.hedge_acc_trans_amort_id;
        if (amortId != null) {
          result = { [amortId]: items };
        }
      } else if (
        response.data &&
        typeof response.data === 'object' &&
        !Array.isArray(response.data)
      ) {
        result = response.data as Record<
          number,
          TransactionAmortSetScheduleData[]
        >;
      } else {
        return rejectWithValue('Invalid response format from API');
      }

      // dispatch into slice
      dispatch(setTransactionAmortSchedules(result));
      return result;
    } catch (err: any) {
      return rejectWithValue(
        err.response?.data?.error_msg ||
          err.message ||
          'Failed to fetch amortization schedules',
      );
    }
  },
);

export const initialAmortEffectiveRate = createAsyncThunk<
  InitAmortEffectiveRateResponse,
  InitAmortEffectiveRatePayload,
  { rejectValue: string; dispatch: AppDispatch }
>(
  'hedgeAccounting/initialAmortEffectiveRate',
  async (params, { rejectWithValue, dispatch }) => {
    try {
      const response =
        await apiInstance.hedgeAccounting.initialAmortEffectiveRate(params);

      let payload: InitAmortEffectiveRateResponse;
      if (response.data && typeof response.data === 'object') {
        if ('success' in response.data) {
          payload = response.data as InitAmortEffectiveRateResponse;
        } else {
          payload = { success: true, data: response.data };
        }
      } else {
        payload = { success: true, data: response.data };
      }

      if (payload.success && Array.isArray(payload.data)) {
        payload.data.forEach((item: HedgeAmortData) =>
          dispatch(upsertLocalAmortRow({ data: item, isNew: true })),
        );
      }

      return payload;
    } catch (error: any) {
      if (error.response?.data?.error_msg) {
        return rejectWithValue(error.response.data.error_msg);
      }
      const message =
        error.response?.data?.message ||
        error.message ||
        'Failed to initialize amort effective rate';
      return rejectWithValue(message);
    }
  },
);

export const fetchTransactionEffectivenessList = createAsyncThunk<
  TransactionEffectivenessData[],
  string | undefined,
  { state: { hedgeAccounting: HedgeAccountingState }; rejectValue: string }
>(
  'hedgeAccounting/fetchTransactionEffectivenessList',
  async (runDate = undefined, { getState, rejectWithValue }) => {
    try {
      const state = getState() as { hedgeAccounting: HedgeAccountingState };
      const { selectedTrades, selectedEntity } = state.hedgeAccounting;

      // Prepare parameters for the API call
      const params: {
        transaction_ids?: string[];
        company_entity_ids?: string[];
        run_date?: string;
      } = {};

      // Only include transaction_ids if there are selected trades
      if (selectedTrades.length > 0) {
        params.transaction_ids = selectedTrades.map((trade) =>
          String(trade.value),
        );
      }

      // Only include company_entity_ids if there are selected entities
      if (selectedEntity.length > 0) {
        params.company_entity_ids = selectedEntity.map((entity) =>
          String(entity.value),
        );
      }

      // Add run_date parameter if provided
      if (runDate) {
        params.run_date = runDate;
      }

      // Check if at least one parameter has values
      if (
        Object.keys(params).length === 0 ||
        (params.transaction_ids === undefined &&
          params.company_entity_ids === undefined)
      ) {
        return rejectWithValue('No trades or entities selected');
      }

      const response =
        await apiInstance.hedgeAccounting.fetchTransactionEffectivenessList(
          params,
        );

      // Check different possible response structures
      // 1. If response is already an array
      if (Array.isArray(response.data)) {
        return response.data;
      }

      // 2. If response has the structure from Axios with nested data.data
      if (
        response.data &&
        response.data.success &&
        Array.isArray(response.data.data)
      ) {
        return response.data.data;
      }

      // If response structure is unexpected, return an empty array
      return [];
    } catch (error: any) {
      return rejectWithValue(
        error.message || 'Failed to fetch transaction effectiveness data',
      );
    }
  },
);

export const createTransactionEffectiveness = createAsyncThunk<
  TransactionEffectivenessData,
  TransactionEffectivenessPayload,
  { rejectValue: string }
>(
  'hedgeAccounting/createTransactionEffectiveness',
  async (payload, { rejectWithValue }) => {
    try {
      const response =
        await apiInstance.hedgeAccounting.createTransactionEffectiveness(
          payload,
        );

      // Handle empty response (200 but no data)
      if (!response.data) {
        return rejectWithValue('Empty response from API');
      }

      // Handle success/data structure
      if (
        typeof response.data === 'object' &&
        'success' in response.data &&
        response.data.success === true &&
        'data' in response.data
      ) {
        // Handle empty data object
        if (!response.data.data || Object.keys(response.data.data).length === 0) {
          return rejectWithValue('Empty data returned from API');
        }
        toastMessage.success('Transaction effectiveness created successfully');
        return response.data.data;
      }
      // Handle direct data response
      else if (typeof response.data === 'object' && !('success' in response.data)) {
        // Handle empty object
        if (Object.keys(response.data).length === 0) {
          return rejectWithValue('Empty data returned from API');
        }
        toastMessage.success('Transaction effectiveness created successfully');
        return response.data;
      }
      // Handle success: false case
      else if (
        typeof response.data === 'object' &&
        'success' in response.data &&
        response.data.success === false
      ) {
        return rejectWithValue(
          response.data.error_msg || 'API returned success: false'
        );
      }

      return rejectWithValue('Invalid response format from API');
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.error_msg ||
          error.message ||
          'Failed to create transaction effectiveness data',
      );
    }
  },
);

export const updateTransactionEffectiveness = createAsyncThunk<
  TransactionEffectivenessData,
  UpdateTransactionEffectivenessPayload,
  { rejectValue: string }
>(
  'hedgeAccounting/updateTransactionEffectiveness',
  async (payload, { rejectWithValue }) => {
    try {
      const response =
        await apiInstance.hedgeAccounting.updateTransactionEffectiveness(
          payload.hedge_acc_trans_eff_id,
          payload.data,
        );

      // Handle empty response (200 but no data)
      if (!response.data) {
        return rejectWithValue('Empty response from API');
      }

      // Handle success/data structure
      if (
        typeof response.data === 'object' &&
        'success' in response.data &&
        response.data.success === true &&
        'data' in response.data
      ) {
        // Handle empty data object
        if (!response.data.data || Object.keys(response.data.data).length === 0) {
          return rejectWithValue('Empty data returned from API');
        }
        toastMessage.success('Transaction effectiveness updated successfully');
        return response.data.data;
      }
      // Handle direct data response
      else if (typeof response.data === 'object' && !('success' in response.data)) {
        // Handle empty object
        if (Object.keys(response.data).length === 0) {
          return rejectWithValue('Empty data returned from API');
        }
        toastMessage.success('Transaction effectiveness updated successfully');
        return response.data;
      }
      // Handle success: false case
      else if (
        typeof response.data === 'object' &&
        'success' in response.data &&
        response.data.success === false
      ) {
        return rejectWithValue(
          response.data.error_msg || 'API returned success: false'
        );
      }

      return rejectWithValue('Invalid response format from API');
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.error_msg ||
          error.message ||
          'Failed to update transaction effectiveness data',
      );
    }
  },
);

export const patchTransactionEffectiveness = createAsyncThunk<
  TransactionEffectivenessData,
  PatchTransactionEffectivenessPayload,
  { rejectValue: string }
>(
  'hedgeAccounting/patchTransactionEffectiveness',
  async (payload, { rejectWithValue }) => {
    try {
      const response =
        await apiInstance.hedgeAccounting.patchTransactionEffectiveness(
          payload.hedge_acc_trans_eff_id,
          payload.data,
        );

      // Handle empty response (200 but no data)
      if (!response.data) {
        return rejectWithValue('Empty response from API');
      }

      // Handle success/data structure
      if (
        typeof response.data === 'object' &&
        'success' in response.data &&
        response.data.success === true &&
        'data' in response.data
      ) {
        // Handle empty data object
        if (!response.data.data || Object.keys(response.data.data).length === 0) {
          return rejectWithValue('Empty data returned from API');
        }
        toastMessage.success('Transaction effectiveness patched successfully');
        return response.data.data;
      }
      // Handle direct data response
      else if (typeof response.data === 'object' && !('success' in response.data)) {
        // Handle empty object
        if (Object.keys(response.data).length === 0) {
          return rejectWithValue('Empty data returned from API');
        }
        toastMessage.success('Transaction effectiveness patched successfully');
        return response.data;
      }
      // Handle success: false case
      else if (
        typeof response.data === 'object' &&
        'success' in response.data &&
        response.data.success === false
      ) {
        return rejectWithValue(
          response.data.error_msg || 'API returned success: false'
        );
      }

      return rejectWithValue('Invalid response format from API');
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.error_msg ||
          error.message ||
          'Failed to patch transaction effectiveness data',
      );
    }
  },
);

export const deleteTransactionEffectiveness = createAsyncThunk<
  number,
  number,
  { rejectValue: string }
>(
  'hedgeAccounting/deleteTransactionEffectiveness',
  async (hedge_acc_trans_eff_id, { rejectWithValue }) => {
    try {
      await apiInstance.hedgeAccounting.deleteTransactionEffectiveness(
        hedge_acc_trans_eff_id,
      );

      toastMessage.success('Transaction effectiveness deleted successfully');

      // Return the ID that was deleted for state updates
      return hedge_acc_trans_eff_id;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.error_msg ||
          error.message ||
          'Failed to delete transaction effectiveness data',
      );
    }
  },
);

export const fetchTransactionRelations = createAsyncThunk<
  TransactionRelation[],
  string | undefined,
  { state: { hedgeAccounting: HedgeAccountingState }; rejectValue: string }
>(
  'hedgeAccounting/fetchTransactionRelations',
  async (runDate = undefined, { getState, rejectWithValue }) => {
    try {
      const state = getState() as { hedgeAccounting: HedgeAccountingState };
      const { selectedTrades, selectedEntity } = state.hedgeAccounting;

      // Prepare parameters for the API call
      const params: {
        transaction_ids?: string[];
        company_entity_ids?: string[];
        run_date?: string;
      } = {};

      // Only include transaction_ids if there are selected trades
      if (selectedTrades.length > 0) {
        params.transaction_ids = selectedTrades.map((trade) =>
          String(trade.value),
        );
      }

      // Only include company_entity_ids if there are selected entities
      if (selectedEntity.length > 0) {
        params.company_entity_ids = selectedEntity.map((entity) =>
          String(entity.value),
        );
      }

      // Add run_date parameter if provided
      if (runDate) {
        params.run_date = runDate;
      }

      // Check if at least one parameter has values
      if (
        Object.keys(params).length === 0 ||
        (params.transaction_ids === undefined &&
          params.company_entity_ids === undefined)
      ) {
        return rejectWithValue('No trades or entities selected');
      }

      const response =
        await apiInstance.hedgeAccounting.fetchTransactionRelations(params);

      // Check different possible response structures
      // 1. If response is already an array
      if (Array.isArray(response.data)) {
        return response.data;
      }

      // 2. If response has the structure from Axios with nested data.data
      if (
        response.data &&
        response.data.success &&
        Array.isArray(response.data.data)
      ) {
        return response.data.data;
      }

      // If response structure is unexpected, return an empty array
      return [];
    } catch (error: any) {
      console.error('Transaction Relations API Error:', error);

      // Specific error handling for backend issues
      if (error.message && error.message.includes('NoneType')) {
        console.error(
          'Backend error with NoneType. This is likely a backend issue with handling None values.',
        );
        // Return empty array instead of rejecting to prevent the app from showing error state
        return [];
      }

      // Handle 400 Bad Request errors (likely validation issues)
      if (error.response && error.response.status === 400) {
        console.error('Validation error from API:', error.response.data);
        // Return empty array instead of rejecting for validation errors
        return [];
      }

      return rejectWithValue(
        error.message || 'Failed to fetch transaction relations data',
      );
    }
  },
);

export const fetchTransactionsWithoutHedgeAcc = createAsyncThunk<
  TransactionWithoutHedgeAcc[],
  string | undefined,
  { state: { hedgeAccounting: HedgeAccountingState }; rejectValue: string }
>(
  'hedgeAccounting/fetchTransactionsWithoutHedgeAcc',
  async (runDate = undefined, { getState, rejectWithValue }) => {
    try {
      const state = getState() as { hedgeAccounting: HedgeAccountingState };
      const { selectedTrades, selectedEntity } = state.hedgeAccounting;

      // Prepare parameters for the API call
      const params: {
        transaction_ids?: string[];
        company_entity_ids?: string[];
        run_date?: string;
      } = {};

      // Only include transaction_ids if there are selected trades
      if (selectedTrades.length > 0) {
        params.transaction_ids = selectedTrades.map((trade) =>
          String(trade.value),
        );
      }

      // Only include company_entity_ids if there are selected entities
      if (selectedEntity.length > 0) {
        params.company_entity_ids = selectedEntity.map((entity) =>
          String(entity.value),
        );
      }

      // Add run_date parameter if provided
      if (runDate) {
        params.run_date = runDate;
      }

      // Check if at least one parameter has values
      if (
        Object.keys(params).length === 0 ||
        (params.transaction_ids === undefined &&
          params.company_entity_ids === undefined)
      ) {
        return rejectWithValue('No trades or entities selected');
      }

      const response =
        await apiInstance.hedgeAccounting.fetchTransactionsWithoutHedgeAcc(
          params,
        );

      // Check different possible response structures
      // 1. If response is already an array
      if (Array.isArray(response.data)) {
        return response.data;
      }

      // 2. If response has the structure from Axios with nested data.data
      if (
        response.data &&
        response.data.success &&
        Array.isArray(response.data.data)
      ) {
        return response.data.data;
      }

      // If response structure is unexpected, return an empty array
      return [];
    } catch (error: any) {
      console.error('Transactions Without Hedge Acc API Error:', error);

      // Handle specific error cases
      if (error.response && error.response.status === 400) {
        console.error('Validation error from API:', error.response.data);
        // Return empty array instead of rejecting for validation errors
        return [];
      }

      return rejectWithValue(
        error.message ||
          'Failed to fetch transactions without hedge accounting data',
      );
    }
  },
);

export const createTransactionNoHedgeAcc = createAsyncThunk<
  TransactionWithoutHedgeAcc,
  CreateTransactionNoHedgeAccPayload,
  { rejectValue: string }
>(
  'hedgeAccounting/createTransactionNoHedgeAcc',
  async (params, { rejectWithValue }) => {
    try {
      const response =
        await apiInstance.hedgeAccounting.createTransactionNoHedgeAcc(params);

      // Handle empty response (200 but no data)
      if (!response.data) {
        return rejectWithValue('Empty response from API');
      }

      // Handle success/data structure
      if (
        typeof response.data === 'object' &&
        'success' in response.data &&
        response.data.success === true &&
        'data' in response.data
      ) {
        // Handle empty data object
        if (!response.data.data || Object.keys(response.data.data).length === 0) {
          return rejectWithValue('Empty data returned from API');
        }
        toastMessage.success('No hedge accounting record created successfully');
        return response.data.data;
      }
      // Handle direct data response
      else if (typeof response.data === 'object' && !('success' in response.data)) {
        // Handle empty object
        if (Object.keys(response.data).length === 0) {
          return rejectWithValue('Empty data returned from API');
        }
        toastMessage.success('No hedge accounting record created successfully');
        return response.data;
      }
      // Handle success: false case
      else if (
        typeof response.data === 'object' &&
        'success' in response.data &&
        response.data.success === false
      ) {
        return rejectWithValue(
          response.data.error_msg || 'API returned success: false'
        );
      }

      return rejectWithValue('Invalid response format from API');
    } catch (error: any) {
      console.error('Create transaction no hedge acc API Error:', error);

      return rejectWithValue(
        error.response?.data?.error_msg ||
          error.message ||
          'Failed to create no hedge accounting record',
      );
    }
  },
);

export const updateTransactionNoHedgeAcc = createAsyncThunk<
  TransactionWithoutHedgeAcc,
  UpdateTransactionNoHedgeAccPayload,
  { rejectValue: string }
>(
  'hedgeAccounting/updateTransactionNoHedgeAcc',
  async (params, { rejectWithValue }) => {
    try {
      const response =
        await apiInstance.hedgeAccounting.updateTransactionNoHedgeAcc(params);

      // Handle empty response (200 but no data)
      if (!response.data) {
        return rejectWithValue('Empty response from API');
      }

      // Handle success/data structure
      if (
        typeof response.data === 'object' &&
        'success' in response.data &&
        response.data.success === true &&
        'data' in response.data
      ) {
        // Handle empty data object
        if (!response.data.data || Object.keys(response.data.data).length === 0) {
          return rejectWithValue('Empty data returned from API');
        }
        toastMessage.success('No hedge accounting record updated successfully');
        return response.data.data;
      }
      // Handle direct data response
      else if (typeof response.data === 'object' && !('success' in response.data)) {
        // Handle empty object
        if (Object.keys(response.data).length === 0) {
          return rejectWithValue('Empty data returned from API');
        }
        toastMessage.success('No hedge accounting record updated successfully');
        return response.data;
      }
      // Handle success: false case
      else if (
        typeof response.data === 'object' &&
        'success' in response.data &&
        response.data.success === false
      ) {
        return rejectWithValue(
          response.data.error_msg || 'API returned success: false'
        );
      }

      return rejectWithValue('Invalid response format from API');
    } catch (error: any) {
      console.error('Update transaction no hedge acc API Error:', error);

      return rejectWithValue(
        error.response?.data?.error_msg ||
          error.message ||
          'Failed to update no hedge accounting record',
      );
    }
  },
);

export const fetchTransactionTitle = createAsyncThunk<
  TransactionTitle,
  TransactionTitlePayload,
  { rejectValue: string }
>(
  'hedgeAccounting/fetchTransactionTitle',
  async (payload, { rejectWithValue }) => {
    try {
      const response = await apiInstance.hedgeAccounting.getTransactionTitle(
        payload.transactionId,
      );

      // Handle empty response (200 but no data)
      if (!response.data) {
        return rejectWithValue('Empty response from API');
      }

      // Handle success/data structure
      if (
        typeof response.data === 'object' &&
        'success' in response.data &&
        response.data.success === true &&
        'data' in response.data
      ) {
        // Handle empty data object
        if (!response.data.data || Object.keys(response.data.data).length === 0) {
          return rejectWithValue('Empty data returned from API');
        }
        return response.data.data;
      }
      // Handle direct data response
      else if (typeof response.data === 'object' && !('success' in response.data)) {
        // Handle empty object
        if (Object.keys(response.data).length === 0) {
          return rejectWithValue('Empty data returned from API');
        }
        return response.data;
      }
      // Handle success: false case
      else if (
        typeof response.data === 'object' &&
        'success' in response.data &&
        response.data.success === false
      ) {
        return rejectWithValue(
          response.data.error_msg || 'API returned success: false'
        );
      }

      return rejectWithValue('Invalid response format from API');
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.error_msg ||
          error.message ||
          'Failed to fetch transaction title',
      );
    }
  },
);

export const fetchTransactionLegs = createAsyncThunk<
  { transactionId: number; legs: TransactionLeg[] },
  { transactionId: number },
  { rejectValue: string }
>(
  'hedgeAccounting/fetchTransactionLegs',
  async ({ transactionId }, { rejectWithValue }) => {
    try {
      const response =
        await apiInstance.hedgeAccounting.fetchTransactionLegs(transactionId);

      // Handle empty response (200 but no data)
      if (!response.data) {
        return rejectWithValue('Empty response from API');
      }

      // Handle success/data/legs structure
      if (
        typeof response.data === 'object' &&
        'success' in response.data &&
        response.data.success === true &&
        'data' in response.data &&
        response.data.data &&
        'legs' in response.data.data &&
        Array.isArray(response.data.data.legs)
      ) {
        return {
          transactionId,
          legs: response.data.data.legs,
        };
      }
      // Handle direct data/legs response
      else if (
        typeof response.data === 'object' &&
        'data' in response.data &&
        response.data.data &&
        'legs' in response.data.data &&
        Array.isArray(response.data.data.legs)
      ) {
        return {
          transactionId,
          legs: response.data.data.legs,
        };
      }
      // Handle success: false case
      else if (
        typeof response.data === 'object' &&
        'success' in response.data &&
        response.data.success === false
      ) {
        return rejectWithValue(
          response.data.error_msg || 'API returned success: false'
        );
      }

      return rejectWithValue('Invalid response format from API');
    } catch (error: any) {
      return rejectWithValue(
        error.message || 'Failed to fetch transaction legs',
      );
    }
  },
);

export const fetchBulkTransactionTitles = createAsyncThunk<
  BulkTransactionTitlesResponse,
  BulkTransactionTitlesRequest,
  { rejectValue: string }
>(
  'hedgeAccounting/fetchBulkTransactionTitles',
  async (payload, { rejectWithValue }) => {
    try {
      const response =
        await apiInstance.hedgeAccounting.getBulkTransactionTitles(payload);

      if (response.data?.success && response.data?.data) {
        return response.data.data;
      }

      return rejectWithValue('Invalid response format from API');
    } catch (error: any) {
      return rejectWithValue(
        error.message || 'Failed to fetch bulk transaction titles',
      );
    }
  },
);

export const fetchTransactionDetail = createAsyncThunk<
  BulkTransactionTitle,
  { transactionId: number },
  { rejectValue: string }
>(
  'hedgeAccounting/fetchTransactionDetail',
  async ({ transactionId }, { rejectWithValue }) => {
    try {
      const response =
        await apiInstance.hedgeAccounting.getTransactionDetail(transactionId);

      if (response.data?.success && response.data?.data) {
        return response.data.data;
      }

      return rejectWithValue('Invalid response format from API');
    } catch (error: any) {
      return rejectWithValue(
        error.message || 'Failed to fetch transaction detail',
      );
    }
  },
);

export const fetchTransactionTitleStructured = createAsyncThunk<
  BulkTransactionTitle,
  { transactionId: number },
  { rejectValue: string }
>(
  'hedgeAccounting/fetchTransactionTitleStructured',
  async ({ transactionId }, { rejectWithValue }) => {
    try {
      const response =
        await apiInstance.hedgeAccounting.getTransactionTitleStructured(transactionId);

      if (response.data?.success && response.data?.data) {
        // Ensure transaction_id is included in the response
        return {
          ...response.data.data,
          transaction_id: transactionId,
        };
      }

      return rejectWithValue('Invalid response format from API');
    } catch (error: any) {
      return rejectWithValue(
        error.message || 'Failed to fetch transaction title structured',
      );
    }
  },
);

export const fetchBulkTransactionTitlesStructured = createAsyncThunk<
  BulkTransactionTitle[],
  { transaction_ids: number[] },
  { rejectValue: string }
>(
  'hedgeAccounting/fetchBulkTransactionTitlesStructured',
  async ({ transaction_ids }, { rejectWithValue }) => {
    try {
      const response =
        await apiInstance.hedgeAccounting.postBulkTransactionTitlesStructured({
          transaction_ids,
        });

      if (response.data?.success && response.data?.data?.transaction_titles) {
        return response.data.data.transaction_titles;
      }

      return rejectWithValue('Invalid response format from API');
    } catch (error: any) {
      return rejectWithValue(
        error.message || 'Failed to fetch bulk transaction titles structured',
      );
    }
  },
);

export const createHypoTrade = createAsyncThunk<
  any,
  {
    transaction_id: number;
    delete_floor_leg?: boolean;
    create_floor_leg?: boolean;
    strike?: number;
    coupon?: number;
  },
  { rejectValue: string }
>(
  'hedgeAccounting/createHypoTrade',
  async (params, { rejectWithValue }) => {
    try {
      const response = await apiInstance.hedgeAccounting.createHypoTrade(params);

      // Handle empty response (200 but no data)
      if (!response.data) {
        return rejectWithValue('Empty response from API');
      }

      // Handle success/data structure
      if (
        typeof response.data === 'object' &&
        'success' in response.data &&
        response.data.success === true &&
        'data' in response.data
      ) {
        toastMessage.success('Hypothetical trade created successfully');
        return response.data.data || response.data;
      }
      // Handle direct data response
      else if (typeof response.data === 'object') {
        // Handle success: false case
        if ('success' in response.data && response.data.success === false) {
          return rejectWithValue(
            response.data.error_msg || 'API returned success: false'
          );
        }
        toastMessage.success('Hypothetical trade created successfully');
        return response.data;
      }

      return rejectWithValue('Invalid response format from API');
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.error_msg ||
          error.message ||
          'Failed to create hypothetical trade',
      );
    }
  },
);

export const getTradeDesignationDate = createAsyncThunk<
  TradeDesignationDate,
  number,
  { rejectValue: string }
>(
  'hedgeAccounting/getTradeDesignationDate',
  async (transactionId, { rejectWithValue }) => {
    try {
      const response =
        await apiInstance.hedgeAccounting.getTradeDesignationDate(
          transactionId,
        );

      // Handle empty response (200 but no data)
      if (!response.data) {
        return rejectWithValue('Empty response from API');
      }

      // Handle success/data structure
      if (
        typeof response.data === 'object' &&
        'success' in response.data &&
        response.data.success === true &&
        'data' in response.data
      ) {
        // Handle empty data object
        if (!response.data.data || Object.keys(response.data.data).length === 0) {
          return rejectWithValue('Empty data returned from API');
        }
        return response.data.data;
      }
      // Handle direct data response
      else if (typeof response.data === 'object' && !('success' in response.data)) {
        // Handle empty object
        if (Object.keys(response.data).length === 0) {
          return rejectWithValue('Empty data returned from API');
        }
        return response.data;
      }
      // Handle success: false case
      else if (
        typeof response.data === 'object' &&
        'success' in response.data &&
        response.data.success === false
      ) {
        return rejectWithValue(
          response.data.error_msg || 'API returned success: false'
        );
      }

      return rejectWithValue('Invalid response format from API');
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.error_msg ||
          error.message ||
          'Failed to fetch trade designation date',
      );
    }
  },
);

export const postTradeDesignationDate = createAsyncThunk<
  { success: boolean; message: string },
  { transaction_id: number; designation_date: string },
  { rejectValue: string }
>(
  'hedgeAccounting/postTradeDesignationDate',
  async (params, { rejectWithValue }) => {
    try {
      const response =
        await apiInstance.hedgeAccounting.postTradeDesignationDate(params);

      // Handle empty response (200 but no data)
      if (!response.data) {
        // Empty response can be treated as success for POST operations
        toastMessage.success('Trade designation date set successfully');
        return {
          success: true,
          message: 'Trade designation date set successfully',
        };
      }

      // Handle success/data structure
      if (typeof response.data === 'object' && 'success' in response.data) {
        // Handle success: false case
        if (response.data.success === false) {
          return rejectWithValue(
            response.data.error_msg || 'API returned success: false'
          );
        }
        toastMessage.success('Trade designation date set successfully');
        return {
          success: response.data.success,
          message:
            response.data.data || 'Trade designation date set successfully',
        };
      }
      // Handle direct response
      else if (typeof response.data === 'object') {
        toastMessage.success('Trade designation date set successfully');
        return {
          success: true,
          message: 'Trade designation date set successfully',
        };
      }

      return rejectWithValue('Invalid response format from API');
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.error_msg ||
          error.message ||
          'Failed to set trade designation date',
      );
    }
  },
);

export const deleteTradeDesignationDate = createAsyncThunk<
  { success: boolean; message: string },
  number,
  { rejectValue: string }
>(
  'hedgeAccounting/deleteTradeDesignationDate',
  async (transactionId, { rejectWithValue }) => {
    try {
      const response =
        await apiInstance.hedgeAccounting.deleteTradeDesignationDate(
          transactionId,
        );

      // Handle empty response (200 but no data)
      if (!response.data) {
        // Empty response can be treated as success for DELETE operations
        toastMessage.success('Trade designation date deleted successfully');
        return {
          success: true,
          message: 'Trade designation date deleted successfully',
        };
      }

      // Handle success/data structure
      if (typeof response.data === 'object' && 'success' in response.data) {
        // Handle success: false case
        if (response.data.success === false) {
          return rejectWithValue(
            response.data.error_msg || 'API returned success: false'
          );
        }
        toastMessage.success('Trade designation date deleted successfully');
        return {
          success: response.data.success,
          message:
            response.data.data ||
            'Trade designation date deleted successfully',
        };
      }
      // Handle direct response
      else if (typeof response.data === 'object') {
        toastMessage.success('Trade designation date deleted successfully');
        return {
          success: true,
          message: 'Trade designation date deleted successfully',
        };
      }

      return rejectWithValue('Invalid response format from API');
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.error_msg ||
          error.message ||
          'Failed to delete trade designation date',
      );
    }
  },
);

export const getIneffectivenessValues = createAsyncThunk(
  'hedgeAccounting/getIneffectivenessValues',
  async (transactionId: number, { rejectWithValue }) => {
    try {
      const response =
        await apiInstance.hedgeAccounting.getIneffectivenessValues(
          transactionId,
        );

      // Handle empty response (200 but no data)
      if (!response.data) {
        return rejectWithValue('Empty response from API');
      }

      // Handle success/data structure
      if (
        typeof response.data === 'object' &&
        'success' in response.data &&
        response.data.success === true &&
        'data' in response.data
      ) {
        // Handle empty data object
        if (!response.data.data || Object.keys(response.data.data).length === 0) {
          return rejectWithValue('Empty data returned from API');
        }
        return response.data.data;
      }
      // Handle direct data response
      else if (typeof response.data === 'object' && !('success' in response.data)) {
        // Handle empty object
        if (Object.keys(response.data).length === 0) {
          return rejectWithValue('Empty data returned from API');
        }
        return response.data;
      }
      // Handle success: false case
      else if (
        typeof response.data === 'object' &&
        'success' in response.data &&
        response.data.success === false
      ) {
        return rejectWithValue(
          response.data.error_msg || 'API returned success: false'
        );
      }

      return rejectWithValue('Invalid response format from API');
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.error_msg ||
          error.message ||
          'Failed to fetch ineffectiveness values',
      );
    }
  },
);

export const postIneffectivenessValues = createAsyncThunk(
  'hedgeAccounting/postIneffectivenessValues',
  async (
    {
      transactionId,
      ineffectivenessValue,
      hypoValue,
    }: {
      transactionId: number;
      ineffectivenessValue?: number | null;
      hypoValue?: number | null;
    },
    { rejectWithValue },
  ) => {
    try {
      const response =
        await apiInstance.hedgeAccounting.postIneffectivenessValues(
          transactionId,
          ineffectivenessValue,
          hypoValue,
        );

      // Handle empty response (200 but no data)
      if (!response || !response.data) {
        // Check if response object has success property directly
        if (response && 'success' in response && response.success === true) {
          toastMessage.success('Ineffectiveness values updated.');
          return {};
        }
        return rejectWithValue('Empty response from API');
      }

      // Handle success/data structure
      if (
        typeof response.data === 'object' &&
        'success' in response.data &&
        response.data.success === true
      ) {
        toastMessage.success('Ineffectiveness values updated.');
        return response.data.data || response.data;
      }
      // Handle direct success on response object
      else if ('success' in response && response.success === true) {
        toastMessage.success('Ineffectiveness values updated.');
        return response.data;
      }
      // Handle success: false case
      else if (
        typeof response.data === 'object' &&
        'success' in response.data &&
        response.data.success === false
      ) {
        return rejectWithValue(
          response.data.error_msg || 'API returned success: false'
        );
      }
      // Handle direct data response
      else if (typeof response.data === 'object') {
        toastMessage.success('Ineffectiveness values updated.');
        return response.data;
      }

      return rejectWithValue('Invalid response format from API');
    } catch (error: any) {
      if (error.response?.data?.error_msg === 'error related to trade_id fields') {
        toastMessage.warning(
          'Hypothetical transaction not found. Please create a hypothetical transaction and try again.',
        );
      }
      return rejectWithValue(
        error.response?.data?.error_msg ||
          error.message ||
          'Failed to post ineffectiveness values',
      );
    }
  },
);

export const fetchJournalEntryMovements = createAsyncThunk<
  JournalEntryMovement[],
  number,
  { rejectValue: string }
>(
  'hedgeAccounting/fetchJournalEntryMovements',
  async (transactionId, { rejectWithValue }) => {
    try {
      const response =
        await apiInstance.hedgeAccounting.getJournalEntryMovements(
          transactionId,
        );

      // The API returns an array of journal entry movements directly
      if (Array.isArray(response.data)) {
        return response.data;
      }

      // Handle nested success/data structure if present
      if (
        response.data &&
        response.data.success &&
        Array.isArray(response.data.data)
      ) {
        return response.data.data;
      }

      return [];
    } catch (error: any) {
      return rejectWithValue(
        error.message || 'Failed to fetch journal entry movements',
      );
    }
  },
);

export const createJournalEntryMovements = createAsyncThunk<
  string,
  CreateJournalEntryMovementsPayload,
  { rejectValue: string; dispatch: AppDispatch }
>(
  'hedgeAccounting/createJournalEntryMovements',
  async ({ transactionId, standardId }, { rejectWithValue, dispatch }) => {
    try {
      const response =
        await apiInstance.hedgeAccounting.createJournalEntryMovements({
          transaction_ids: [transactionId],
          standard_id: standardId,
        });

      // Handle empty response (200 but no data) - treat as success for POST
      if (!response.data) {
        dispatch(fetchJournalEntryMovements(transactionId));
        return 'OK';
      }

      // Handle success/data structure
      if (
        typeof response.data === 'object' &&
        'success' in response.data &&
        response.data.success === true
      ) {
        // Handle success: false case
        if (response.data.success === false) {
          const message = response.data.error_msg || 'API returned success: false';
          toastMessage.error(`Failed to create journal entry movement: ${message}`);
          return rejectWithValue(message);
        }
        dispatch(fetchJournalEntryMovements(transactionId));
        return 'OK';
      }
      // If it's directly a string
      else if (typeof response.data === 'string') {
        dispatch(fetchJournalEntryMovements(transactionId));
        return 'OK';
      }
      // Handle direct data response
      else if (typeof response.data === 'object') {
        dispatch(fetchJournalEntryMovements(transactionId));
        return 'OK';
      }

      return rejectWithValue('Invalid response format from API');
    } catch (error: any) {
      const message = getThunkError(error);
      toastMessage.error(`Failed to create journal entry movement: ${message}`);
      return rejectWithValue(
        error.response?.data?.error_msg ||
          error.response?.data?.message ||
          error.message ||
          'Failed to create journal entry movements',
      );
    }
  },
);

export const editJournalEntryMovementComment = createAsyncThunk(
  'hedgeAccounting/editJournalEntryMovementComment',
  async (
    {
      transactionId,
      jeMovementId,
      comment,
    }: EditJournalEntryMovementCommentPayload,
    { dispatch, rejectWithValue },
  ) => {
    try {
      const response =
        await apiInstance.hedgeAccounting.editJournalEntryMovementComment(
          transactionId,
          jeMovementId,
          comment,
        );

      // Handle empty response (200 but no data) - treat as success for PUT/PATCH
      if (!response.data) {
        dispatch(fetchJournalEntryMovements(transactionId));
        return 'OK';
      }

      // Handle success/data structure
      if (
        typeof response.data === 'object' &&
        'success' in response.data
      ) {
        // Handle success: false case
        if (response.data.success === false) {
          return rejectWithValue(
            response.data.error_msg || 'API returned success: false'
          );
        }
        dispatch(fetchJournalEntryMovements(transactionId));
        return 'OK';
      }
      // Handle string response
      else if (
        response.data === 'OK' ||
        (typeof response.data === 'string' && response.data.includes('OK'))
      ) {
        dispatch(fetchJournalEntryMovements(transactionId));
        return 'OK';
      }
      // Handle any other response as success
      else if (response.data) {
        dispatch(fetchJournalEntryMovements(transactionId));
        return 'OK';
      }

      return rejectWithValue('Invalid response format from API');
    } catch (error: any) {
      console.error('Error editing journal entry movement comment:', error);
      return rejectWithValue(
        error.response?.data?.error_msg ||
          error.message ||
          'Failed to edit journal entry movement comment',
      );
    }
  },
);

export const getCompanyEntityHedgingStandard = createAsyncThunk<
  CompanyEntityHedgingStandard,
  string,
  { rejectValue: string }
>(
  'hedgeAccounting/getCompanyEntityHedgingStandard',
  async (companyEntityId, { rejectWithValue }) => {
    try {
      const response =
        await apiInstance.hedgeAccounting.getCompanyEntityHedgingStandard(
          companyEntityId,
        );

      // Handle empty response (200 but no data)
      if (!response.data) {
        return rejectWithValue(
          `Hedge accounting standard for company entity with ID '${companyEntityId}' not defined.`,
        );
      }

      // Handle success response
      if (typeof response.data === 'object') {
        // Handle success: false case
        if ('success' in response.data && response.data.success === false) {
          return rejectWithValue(
            response.data.error_msg ||
            `Hedge accounting standard for company entity with ID '${companyEntityId}' not defined.`,
          );
        }

        // If it has success/data structure, return the data
        if ('success' in response.data && response.data.success === true && response.data.data) {
          // Handle empty data object
          if (Object.keys(response.data.data).length === 0) {
            return rejectWithValue(
              `Hedge accounting standard for company entity with ID '${companyEntityId}' not defined.`,
            );
          }
          return response.data.data;
        }

        // If it's already a CompanyEntityHedgingStandard, return it directly
        if ('hedge_acc_standard' in response.data) {
          return response.data as CompanyEntityHedgingStandard;
        }
      }

      return rejectWithValue(
        `Hedge accounting standard for company entity with ID '${companyEntityId}' not defined.`,
      );
    } catch (error: any) {
      const errorMsg =
        error.response?.data?.error_msg ||
        `Hedge accounting standard for company entity with ID '${companyEntityId}' not defined.`;
      return rejectWithValue(errorMsg);
    }
  },
);

export const createJournalEntries = createAsyncThunk<
  JournalEntry[],
  JournalEntryCommand[],
  { rejectValue: string }
>(
  'hedgeAccounting/createJournalEntries',
  async (commands, { rejectWithValue }) => {
    try {
      const response =
        await apiInstance.hedgeAccounting.createOrUpdateJournalEntries(
          commands,
        );

      // Handle different response formats
      if (Array.isArray(response.data)) {
        return response.data;
      }

      // Handle nested success/data structure if present
      if (
        response.data &&
        response.data.success &&
        Array.isArray(response.data.data)
      ) {
        return response.data.data;
      }

      // If response structure is unexpected, return empty array and log warning
      console.warn(
        'Unexpected response format from createJournalEntries:',
        response.data,
      );
      return [];
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message ||
          error.message ||
          'Failed to create journal entries',
      );
    }
  },
);

// Create and Reset Journal Entries - combines create with selection reset
export const createAndResetJournalEntries = createAsyncThunk(
  'hedgeAccounting/createAndResetJournalEntries',
  async (
    payload: {
      tradeId: number;
      jeDate: string | null;
      selectedTypes: { label: string; value: number }[];
    },
    { dispatch, rejectWithValue, getState },
  ) => {
    try {
      if (!payload.tradeId) {
        throw new Error('Cannot add journal entries: No trade ID available');
      }

      // Create the commands array with the selected journal entry types
      const commands = [
        {
          row_status: 'new', // new, update, delete
          trade_id: Number(payload.tradeId),
          je_date: payload.jeDate,
          je_status_id: 2, // Default status ID
          je_comment: '',
          entries: payload.selectedTypes.map((type) => ({
            je_type: type.label,
            je_amount: 0, // Default amount
          })),
        },
      ];

      // Create journal entries using the existing thunk
      await dispatch(createJournalEntries(commands)).unwrap();

      // Fetch updated journal entries for this trade
      await (dispatch as any)(
        fetchJournalEntries(String(payload.tradeId)),
      ).unwrap();

      // Get the latest state after fetching
      const state = getState() as { hedgeAccounting: HedgeAccountingState };

      return {
        journalEntries: state.hedgeAccounting.journalEntries,
      };
    } catch (error: any) {
      console.error('Failed to create journal entries:', error);
      return rejectWithValue(
        error.message || 'Failed to create journal entries',
      );
    }
  },
);

export const deleteTransactionAmortSetSchedule = createAsyncThunk<
  void,
  { scheduleId: number; amortId: number },
  { rejectValue: string; dispatch: AppDispatch }
>(
  'hedgeAccounting/deleteTransactionAmortSetSchedule',
  async ({ scheduleId, amortId }, { rejectWithValue, dispatch }) => {
    try {
      // 1) Delete the schedule entry
      await apiInstance.hedgeAccounting.deleteTransactionAmortSetSchedule(
        scheduleId,
      );
      // 2) Reload remaining schedules for this amortization
      dispatch(
        searchTransactionAmortSetSchedule({
          hedge_acc_trans_amort_id: amortId,
        }),
      );
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.error_msg ||
          error.message ||
          'Failed to delete transaction amortization schedule',
      );
    }
  },
);

export const updateTransactionAmortSetSchedule = createAsyncThunk<
  void,
  {
    scheduleId: number;
    amortId: number;
    data: {
      hedge_acc_trans_amort_id: number;
      period_start_date: string;
      period_end_date: string;
      trans_amort_amount: number;
    };
  },
  { rejectValue: string; dispatch: AppDispatch }
>(
  'hedgeAccounting/updateTransactionAmortSetSchedule',
  async ({ scheduleId, amortId, data }, { rejectWithValue, dispatch }) => {
    try {
      await apiInstance.hedgeAccounting.updateTransactionAmortSetSchedule(
        scheduleId,
        data,
      );

      // 2) Reload all schedules for that amort ID
      dispatch(
        searchTransactionAmortSetSchedule({
          hedge_acc_trans_amort_id: amortId,
        }),
      );
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.error_msg ||
          error.message ||
          'Failed to update transaction amortization schedule',
      );
    }
  },
);

export const createReplicaHypothetical = createAsyncThunk<
  void,
  { transaction_id: string },
  { rejectValue: string; dispatch: AppDispatch }
>(
  'hedgeAccounting/createReplicaHypothetical',
  async ({ transaction_id }, { rejectWithValue, dispatch }) => {
    try {
      const response = await apiInstance.hedgeAccounting.createReplicaHypothetical(
        transaction_id,
      );

      // Check for failure response
      if (
        response?.data &&
        typeof response.data === 'object' &&
        'success' in response.data &&
        response.data.success === false
      ) {
        return rejectWithValue(
          response.data.error_msg || 'Failed to create hypothetical replica'
        );
      }

      toastMessage.success('Hypothetical replica created successfully');
      dispatch(fetchTransactionRelations());
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.error_msg ||
          error.message ||
          'Failed to create hypothetical replica',
      );
    }
  },
);

export const deleteHypotheticalTrade = createAsyncThunk<
  void,
  { transaction_relationship_id: string; child_transaction_id: number },
  { rejectValue: string; dispatch: AppDispatch }
>(
  'hedgeAccounting/deleteHypotheticalTrade',
  async ({ transaction_relationship_id, child_transaction_id }, { rejectWithValue, dispatch }) => {
    try {
      const response = await apiInstance.hedgeAccounting.deleteHypotheticalTransaction(
        transaction_relationship_id,
      );

      // Clear regression values for the child transaction after deleting the relationship
      await apiInstance.hedgeAccounting.clearTransactionRegression(
        child_transaction_id,
      );

      dispatch(fetchTransactionRelations());
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.error_msg ||
          error.message ||
          'Failed to delete hypothetical trade',
      );
    }
  },
);

export const onClearTransactionRegression = createAsyncThunk<
  void,
  { parent_transaction_id: number; child_transaction_id: number },
  { rejectValue: string; dispatch: AppDispatch }
>(
  'hedgeAccounting/clearRegressionValues',
  async ({ parent_transaction_id, child_transaction_id }, { rejectWithValue, dispatch }) => {
    toastMessage.info('Clearing Regression Values');
    try {
      // Clear regression values for parent transaction
      const parentResp =
        await apiInstance.hedgeAccounting.clearTransactionRegression(
          parent_transaction_id,
        );

      // Clear regression values for child transaction
      const childResp =
        await apiInstance.hedgeAccounting.clearTransactionRegression(
          child_transaction_id,
        );

      if ((parentResp.data?.success === true || parentResp.data?.data === 'success') &&
          (childResp.data?.success === true || childResp.data?.data === 'success')) {
        toastMessage.success('Regression Values cleared successfully for both transactions');
      }

      dispatch(fetchTransactionRelations());
    } catch (error: any) {
      toastMessage.error('Failed to clear regression values');
      return rejectWithValue(
        error.response?.data?.error_msg ||
          error.message ||
          'Failed to clear regression values',
      );
    }
  },
);

export const updateTransactionFields = createAsyncThunk<
  BulkTransactionTitle,
  UpdateTransactionFieldsPayload,
  { rejectValue: string }
>(
  'hedgeAccounting/updateTransactionFields',
  async (payload, { rejectWithValue }) => {
    try {
      const response =
        await apiInstance.hedgeAccounting.updateTransactionFields(payload);

      // Handle empty response (200 but no data)
      if (!response.data) {
        return rejectWithValue('Empty response from API');
      }

      // Handle success/data structure
      if (
        typeof response.data === 'object' &&
        'success' in response.data &&
        response.data.success === true &&
        'data' in response.data
      ) {
        // Handle empty data object
        if (!response.data.data || Object.keys(response.data.data).length === 0) {
          return rejectWithValue('Empty data returned from API');
        }

        const transactionData = response.data.data.transaction_data;
        toastMessage.success('Transaction fields updated successfully');
        return transactionData;
      }
      // Handle success: false case
      else if (
        typeof response.data === 'object' &&
        'success' in response.data &&
        response.data.success === false
      ) {
        return rejectWithValue(
          response.data.error_msg || 'API returned success: false'
        );
      }

      return rejectWithValue('Invalid response format from API');
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.error_msg ||
          error.message ||
          'Failed to update transaction fields',
      );
    }
  },
);
